import { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ImageOff, ShoppingBag } from 'lucide-react';
import type { StorefrontVariant } from '@mlm/shared';
import { fmtPeso, productImageUrl, store } from '../lib/api';
import { qk } from '../lib/queries';
import { useCartStore } from '../lib/cart-store';
import { Block, CoachButton, Eyebrow, ErrorNote, Loading, Screen } from '../components/primitives';

/**
 * One product. A variant product cannot be added until a variant is chosen,
 * because the price and the stock both belong to the variant, not the product.
 */
export function ShopProductPage() {
  const { id = '' } = useParams();
  const [variantId, setVariantId] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const add = useCartStore((s) => s.add);

  const ctx = useQuery({
    queryKey: qk.shopContext,
    queryFn: store.paymentMethods,
    staleTime: 5 * 60_000,
  });
  const q = useQuery({
    queryKey: qk.shopProduct(id),
    queryFn: () => store.product(id, ctx.data?.slotId ?? null),
    enabled: Boolean(id) && !ctx.isPending,
  });

  const product = q.data?.product;
  const variants: StorefrontVariant[] = useMemo(() => product?.variants ?? [], [product]);
  const variant = variants.find((v) => v.id === variantId) ?? null;
  const needsVariant = variants.length > 0 && !variant;
  const price = variant?.price ?? product?.priceEffective ?? '0';
  const stock = variant ? variant.stockQty : (product?.stockQty ?? null);
  const soldOut = stock !== null && stock <= 0;

  if (!id) return <Navigate to="/shop" replace />;
  if (q.isPending || ctx.isPending) return <Loading label="Loading" />;
  if (q.error) return <ErrorNote error={q.error} retry={() => void q.refetch()} />;
  if (!product) return <Navigate to="/shop" replace />;

  return (
    <Screen back="/shop" eyebrow="Shop" title={product.name}>
      <div className="border-b border-border px-5 py-6">
        <div className="aspect-square w-full overflow-hidden border border-border bg-surface">
          {product.images.length > 0 ? (
            <img
              src={productImageUrl(product.id, 0)}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sage-deep">
              <ImageOff className="h-8 w-8" aria-hidden />
            </div>
          )}
        </div>
        <p className="mt-5 font-display text-[1.6rem] font-semibold text-ink">{fmtPeso(price)}</p>
        {product.pv !== '0.0000' && <Eyebrow className="mt-1">{product.pv} PV</Eyebrow>}
        {product.freeShipping && <Eyebrow className="mt-1">Free shipping</Eyebrow>}
        {soldOut && <Eyebrow className="mt-1">Sold out</Eyebrow>}
      </div>

      {variants.length > 0 && (
        <Block eyebrow="Choose" title="Option">
          <ul className="mt-3 flex flex-wrap gap-2">
            {variants.map((v) => (
              <li key={v.id}>
                <button
                  type="button"
                  disabled={!v.inStock}
                  onClick={() => setVariantId(v.id)}
                  className={[
                    'min-h-11 border px-4 py-2 font-mono text-[0.68rem] uppercase tracking-[0.14em] transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                    v.id === variantId
                      ? 'border-verde bg-surface text-ink'
                      : 'border-[rgba(16,32,26,0.22)] text-ink hover:border-[rgba(16,32,26,0.5)]',
                  ].join(' ')}
                >
                  {v.name}
                </button>
              </li>
            ))}
          </ul>
        </Block>
      )}

      {product.description && <Block title="About this product">{product.description}</Block>}

      <div className="px-5 py-6">
        <div className="flex items-center gap-4">
          <Eyebrow>Quantity</Eyebrow>
          <div className="flex items-center gap-3">
            <CoachButton onClick={() => setQty((n) => Math.max(1, n - 1))} aria-label="Less">
              &minus;
            </CoachButton>
            <span className="min-w-8 text-center text-[0.95rem] text-ink">{qty}</span>
            <CoachButton onClick={() => setQty((n) => n + 1)} aria-label="More">
              +
            </CoachButton>
          </div>
        </div>

        <CoachButton
          kind="gold"
          block
          className="mt-6"
          disabled={soldOut || needsVariant}
          onClick={() => {
            add(
              {
                productId: product.id,
                variantId: variant?.id ?? null,
                name: product.name,
                variantName: variant?.name ?? null,
                unitPrice: price,
              },
              qty,
            );
            setAdded(true);
          }}
        >
          <ShoppingBag className="h-4 w-4" aria-hidden />
          {soldOut ? 'Sold out' : needsVariant ? 'Choose an option' : 'Add to cart'}
        </CoachButton>

        {added && (
          <p className="mt-4 text-center text-[0.93rem] text-ink/70">
            Added.{' '}
            <Link to="/cart" className="text-sage underline underline-offset-4 hover:text-ink">
              Go to cart
            </Link>
          </p>
        )}
      </div>
    </Screen>
  );
}
